/*
 * Smooth scrolling on the product page, for trackpads and mice (desktop).
 *
 * Trackpads hand the browser uneven steps, so the details column (moving
 * beside the pinned photo) scrolled in visible jerks. Lenis (MIT,
 * darkroom.engineering) eases the page's own scroll position once per
 * display frame, so motion is even at the screen's refresh rate. It drives
 * native scrolling: sticky elements, keyboard, find-in-page and focus all
 * behave as before. Phones, touch screens and reduced-motion visitors keep
 * native scrolling untouched; nested scrollers (thumbnail rail, dialogs,
 * drawers, search results, the zoom viewer) keep their own.
 *
 * Safari keeps its own scrolling too. It scrolls on a separate thread at the
 * screen's full rate (120Hz on ProMotion Macs), but holds the page's own
 * frames to 60fps (30 in Low Power Mode), so easing the scroll from script
 * there can only make it choppier than Safari's own.
 */
(() => {
  const desktopPointer = window.matchMedia('(hover: hover) and (pointer: fine) and (min-width: 990px)');
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const safari = /Apple/.test(navigator.vendor);
  const script = document.currentScript;
  const src = script && script.dataset.lenisSrc;
  if (!src || safari || !desktopPointer.matches || reducedMotion.matches) return;

  const start = () => {
    if (!window.Lenis) return;
    const lenis = new window.Lenis({
      autoRaf: true,
      // 0.18 catches up with the fingers in about 190ms (0.12 took ~300ms and
      // felt slow); a touch more distance per swipe than native.
      lerp: 0.18,
      wheelMultiplier: 1.1,
      smoothWheel: true,
      syncTouch: false,
      allowNestedScroll: true,
      // Anything that scrolls or zooms on its own keeps native wheel handling.
      prevent: (node) => Boolean(node.closest && node.closest(
        '.pswp, dialog[open], [data-search-modal], .bag-drawer, .site-header__drawer, .site-nav__panel, .pdp-cond, .pdp-pass, [data-lenis-prevent]'
      ))
    });

    // Pause while something locks the page (the search modal fixes <body>,
    // the bag drawer sets bag-lock, the menu sheet sets menu-lock, the product
    // page's dialogs, Seel's pop-up and the zoom viewer set theirs), and pick up
    // from wherever the page is when it unlocks.
    const root = document.documentElement;
    const LOCKS = ['search-lock', 'bag-lock', 'menu-lock', 'pdp-cond-open', 'pdp-pass-open', 'ldj-seel-open', 'pdp-zoom-open'];
    const sync = () => {
      if (LOCKS.some((name) => root.classList.contains(name))) lenis.stop();
      else lenis.start();
    };
    new MutationObserver(sync).observe(root, { attributes: true, attributeFilter: ['class'] });

    // Screen size or input changes (e.g. docking a tablet): hand back to native.
    const release = () => {
      if (!desktopPointer.matches || reducedMotion.matches) lenis.destroy();
    };
    desktopPointer.addEventListener?.('change', release);
    reducedMotion.addEventListener?.('change', release);
  };

  const tag = document.createElement('script');
  tag.src = src;
  tag.async = true;
  tag.onload = start;
  document.head.append(tag);
})();
